import React, { useState } from "react";
import { Col, Row } from "reactstrap";
import FormInput from "../../../../../../toolkit/formInput";
import WizardFormStep from "../../components/WizardFormStep";
import WizardFooter from "../../../../../../toolkit/wizardFooter";
import * as yup from "yup";
import { createDomainField, extractDomain } from "../../../../../../utils/validation/validationUtils";
import { useLoading } from "../../../../../../hooks/useLoading";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import ICharityEmailSetup from "../../../../../../types/ICharityEmailSetup";
import { useFormContext } from "react-hook-form";
import { DomainService } from "../../../../../../services/domainService";
import { AlertColor } from "@mui/material";
import ErrorAlert from "../../../../../../toolkit/errorAlert";

export const domainInputStepSchema = yup.object({
  sendingDomain: createDomainField("Sending Domain")
    .transform((value) => {
      if (!value) return value;
      return extractDomain(value);
    }),
});

interface DomainInputStepProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  emailSetupData?: ICharityEmailSetup | null;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
}

const DomainInputStep: React.FC<DomainInputStepProps> = ({
  charityId,
  config,
  serviceAccessToken,
  showSnackbar,
}) => {
  const { loading: isSettingUpDomain, runWithLoading } = useLoading();
  const { setValue } = useFormContext();
  const [apiError, setApiError] = useState<string | null>(null);

  const handleNext = async (formData: any) => {
    setApiError(null);

    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      const message =
        "Unable to connect to the admin service. Please refresh and try again.";
      setApiError(message);
      showSnackbar?.(message, "error");
      throw new Error(message);
    }

    await runWithLoading(async () => {
      try {
        const domain = extractDomain(formData.sendingDomain);

        const dnsResponse = await DomainService.setupDomain(
          config,
          charityId,
          domain,
          serviceAccessToken
        );

        setValue("domainResponse", dnsResponse);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to set up domain. Please try again.";
        setApiError(message);
        showSnackbar?.(message, "error");
        throw error;
      }
    });
  };

  return (
    <WizardFormStep
      title="Connect Your Domain"
      subtitle="Please enter your domain to set up email services"
    >
      <Row className="mt-4 justify-content-center">
        <Col md="8">
          <FormInput
            name="sendingDomain"
            type="text"
            icon="fa-solid fa-share-nodes"
            placeholder="Sending Domain (e.g., example.com)"
          />
        </Col>
      </Row>

      {apiError && (
        <Row className="justify-content-center mt-3">
          <Col md="8">
            <ErrorAlert message={apiError} />
          </Col>
        </Row>
      )}

      <WizardFooter
        schema={domainInputStepSchema}
        onNext={handleNext}
        loading={isSettingUpDomain}
        loadingText="Setting up domain..."
        loadingAnimation="pulse"
      />
    </WizardFormStep>
  );
};

export default DomainInputStep;
